import React, { useRef, useState } from "react";
import { Modal, Button, Form, Table, Spinner } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  importSaleEinvoice,
  listSales,
  showCustomerUser,
} from "../erpClient";
import {
  isSalesApiSuccess,
  normalizeListSalesRows,
} from "../utils/salesErpAdapter";
import {
  buildGstEInvoiceJson,
  downloadGstEInvoiceFile,
  gstDocNumbersMatch,
  isValidBuyerGstin,
  mapExcelRowToEinvoicePayload,
  parseGstPortalExcel,
  resolveBuyerGstin,
} from "../utils/gstEInvoice";

function pickCustomerGstin(raw = {}) {
  const g = String(
    raw.GST_number ||
      raw.gst_number ||
      raw.gstNumber ||
      raw.gstin ||
      raw.GSTIN ||
      "",
  )
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
  return isValidBuyerGstin(g) ? g : "";
}

function extractCustomerUserFromResponse(response) {
  const data = response?.data;
  if (!data) return null;
  return (
    data.customer_user ??
    data.customerUser ??
    data.customer ??
    data.user ??
    data.item ??
    (Array.isArray(data.customers) ? data.customers[0] : null) ??
    (Array.isArray(data.items) ? data.items[0] : null) ??
    (Array.isArray(data.data) ? data.data[0] : null) ??
    (Array.isArray(data) ? data[0] : null) ??
    (data.id != null && (data.name != null || data.mobile != null || data.GST_number || data.gst_number)
      ? data
      : null)
  );
}

async function fetchCustomerGstin(customerId) {
  if (customerId == null || customerId === "") return "";
  try {
    const response = await showCustomerUser({ id: Number(customerId) });
    return pickCustomerGstin(extractCustomerUserFromResponse(response) || {});
  } catch {
    return "";
  }
}

const GstPortalModal = ({
  show,
  onHide,
  selectedInvoiceIds = [],
  displayedInvoices = [],
  buildInvoiceWithDetails,
  onImported,
}) => {
  const fileInputRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [excelName, setExcelName] = useState("");
  const [parsedRows, setParsedRows] = useState([]);
  const [parseError, setParseError] = useState("");

  const resetExcel = () => {
    setExcelName("");
    setParsedRows([]);
    setParseError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    resetExcel();
    onHide();
  };

  const handleGenerateJson = async () => {
    if (!selectedInvoiceIds.length) {
      toast.error("Select one or more invoices first");
      return;
    }
    setGenerating(true);
    try {
      const selected = displayedInvoices.filter((inv) =>
        selectedInvoiceIds.includes(inv.id),
      );
      const enriched = [];
      for (const inv of selected) {
        let row = buildInvoiceWithDetails
          ? await buildInvoiceWithDetails(inv)
          : inv;
        // B2B IRN rejects URP — pull GSTIN from customer master if sale row lacks it
        if (!resolveBuyerGstin(row)) {
          const customerId =
            row.userId ??
            row.user_id ??
            row.customer_id ??
            row.customer_user_id ??
            row.customerId ??
            row.userss?.id ??
            row.customer_user?.id ??
            row.customer?.id ??
            inv.userId ??
            inv.customer_id ??
            inv.customer_user_id;
          const gstin = await fetchCustomerGstin(customerId);
          if (gstin) {
            row = {
              ...row,
              customer_gst_number: gstin,
              GST_number: gstin,
              customerDetails: {
                ...(row.customerDetails || {}),
                gst_number: gstin,
                GST_number: gstin,
              },
              userss: {
                ...(row.userss || {}),
                GST_number: gstin,
                gst_number: gstin,
              },
            };
          }
        }
        enriched.push(row);
      }
      const payload = buildGstEInvoiceJson(enriched);
      if (!payload.length) {
        toast.error("No invoice data to generate");
        return;
      }
      const filename = downloadGstEInvoiceFile(payload, enriched);
      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to generate JSON");
    } finally {
      setGenerating(false);
    }
  };

  const handleExcelChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      resetExcel();
      return;
    }
    const name = String(file.name || "").toLowerCase();
    if (!name.endsWith(".xlsx") && !name.endsWith(".xls")) {
      toast.error("Upload the GST portal Excel (.xlsx)");
      event.target.value = "";
      return;
    }
    setParsing(true);
    setParseError("");
    try {
      await new Promise((resolve) => setTimeout(resolve, 40));
      const rows = await parseGstPortalExcel(file);
      if (!rows.length) {
        const message =
          "No invoice rows found in the Excel file. Check that it is the GST portal export with Doc No / IRN columns.";
        setParseError(message);
        toast.error(message);
        setExcelName(file.name);
        setParsedRows([]);
        return;
      }
      setExcelName(file.name);
      setParsedRows(rows);
      setParseError("");
      toast.success(`Read ${rows.length} invoice row(s)`);
    } catch (err) {
      console.error(err);
      const message = err?.message || "Could not read the Excel file";
      setParseError(message);
      toast.error(message);
      setExcelName(file.name);
      setParsedRows([]);
    } finally {
      setParsing(false);
    }
  };

  const findSaleIdForDoc = async (docNo) => {
    const wanted = String(docNo || "").trim();
    if (!wanted) return null;
    const matchesDoc = (inv) =>
      gstDocNumbersMatch(inv.voucherNumber || inv.doc_number || "", wanted);
    const local = displayedInvoices.find(matchesDoc);
    if (local?.id != null) return local.id;
    // Portal sample uses "1285"; app stores "SAL_1285"
    const searchVariants = [
      wanted,
      `SAL_${wanted}`,
      `SAL-${wanted}`,
      wanted.replace(/-/g, "_"),
      wanted.replace(/_/g, "-"),
    ].filter((v, i, arr) => v && arr.indexOf(v) === i);
    for (const term of searchVariants) {
      const res = await listSales({ search: term, page: 1, limit: 20 });
      const rows = normalizeListSalesRows(res);
      const match = rows.find(matchesDoc);
      if (match?.id != null) return match.id;
    }
    return null;
  };

  const handleSaveExcel = async () => {
    if (!parsedRows.length) {
      toast.error("Upload the GST portal Excel first");
      return;
    }
    setSaving(true);
    try {
      const missing = [];
      const failed = [];
      let savedCount = 0;
      for (const row of parsedRows) {
        const lookedUpId = await findSaleIdForDoc(row.doc_no);
        const saleId =
          lookedUpId ??
          (selectedInvoiceIds.length === 1 ? selectedInvoiceIds[0] : null);
        if (saleId == null) {
          missing.push(row.doc_no || "(blank)");
          continue;
        }
        const response = await importSaleEinvoice(
          mapExcelRowToEinvoicePayload(row, saleId),
        );
        const saved = response?.data?.saved !== false;
        if (!isSalesApiSuccess(response) || !saved) {
          failed.push(row.doc_no || String(saleId));
          continue;
        }
        savedCount += 1;
      }
      if (savedCount === 0) {
        toast.error(
          missing.length
            ? `No matching sales found for Doc No: ${missing.join(", ")}`
            : failed.length
              ? `Failed to save: ${failed.join(", ")}`
              : "Nothing to save",
        );
        return;
      }
      const extra = [
        missing.length ? `Not found: ${missing.join(", ")}` : "",
        failed.length ? `Failed: ${failed.join(", ")}` : "",
      ]
        .filter(Boolean)
        .join(". ");
      toast.success(
        extra
          ? `Saved GST details for ${savedCount} invoice(s). ${extra}`
          : `Saved GST details for ${savedCount} invoice(s)`,
      );
      resetExcel();
      if (typeof onImported === "function") onImported();
      onHide();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to save GST portal details");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="fw-semibold">GST Portal</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted mb-3" style={{ fontSize: "0.9rem" }}>
          Generate e-invoice JSON for selected bills, upload it on the GST
          portal, then import the Excel the portal returns so IRN / Ack No are
          stored on the sale.
        </p>
        <div className="d-flex flex-wrap gap-2 mb-4">
          <Button
            variant="primary"
            onClick={handleGenerateJson}
            disabled={generating}
          >
            {generating ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Generating…
              </>
            ) : (
              "Generate JSON"
            )}
          </Button>
        </div>
        <hr />
        <Form.Group>
          <Form.Label className="fw-semibold">Upload Excel </Form.Label>
          <Form.Control
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleExcelChange}
            disabled={parsing || saving}
          />
          {excelName ? (
            <div className="small text-muted mt-1">{excelName}</div>
          ) : null}
          {parsing ? (
            <div className="small text-primary mt-2">Reading Excel…</div>
          ) : null}
          {parseError ? (
            <div className="small text-danger mt-2">{parseError}</div>
          ) : null}
          {!parsing && excelName && parsedRows.length === 0 && !parseError ? (
            <div className="small text-warning mt-2">
              Excel selected but no rows were loaded. Re-select the file or use
              the GST portal export format.
            </div>
          ) : null}
        </Form.Group>
        {parsedRows.length > 0 ? (
          <div className="table-responsive mt-3" style={{ maxHeight: 260 }}>
            <Table bordered size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Doc No</th>
                  <th>IRN</th>
                  <th>Ack No</th>
                  <th>Ack Date</th>
                  <th>GSTIN</th>
                </tr>
              </thead>
              <tbody>
                {parsedRows.map((row, idx) => (
                  <tr key={`${row.doc_no}-${idx}`}>
                    <td>{row.doc_no}</td>
                    <td
                      style={{
                        maxWidth: 160,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={row.irn}
                    >
                      {row.irn}
                    </td>
                    <td>{row.ack_no}</td>
                    <td>{row.ack_date}</td>
                    <td>{row.recipient_gstin}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          onClick={handleSaveExcel}
          disabled={saving || !parsedRows.length}
        >
          {saving ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Saving…
            </>
          ) : (
            "Save to sales"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default GstPortalModal;
